import { handle } from 'redux-pack';

import type  { Action } from '../../../types/actions';
import { SET_SHOW_ACTIVITY_LOG_MODAL, FETCH_ACTIVITY_LOG, SET_MORE_ACTIVITIES_AVAILABLE, RESET_ACTIVITY_LOG } from '../../actions/admin/activity-log';
import type { IReturnableActivity } from "types/working-model";

interface IActivityLogState {
  loadingActivityLog: boolean;
  activityLog: IReturnableActivity[] | null;
  mostRecentActivity: IReturnableActivity | null;
  showActivityLogModal: boolean;
  activityLogCursor: number | undefined;
  noMoreActivities: boolean;
}

const initialState = {
  loadingActivityLog: false,
  activityLog: null,
  mostRecentActivity: null,
  showActivityLogModal: false,
  activityLogCursor: undefined,
  noMoreActivities: false,
};

export default function ActivityLogReducer(state: IActivityLogState = initialState, action: Action): IActivityLogState {
	switch (action.type) {
		case SET_SHOW_ACTIVITY_LOG_MODAL: {
			return {
				...state,
				showActivityLogModal: action.payload
			};
		}
		case SET_MORE_ACTIVITIES_AVAILABLE: {
			return {
				...state,
				noMoreActivities: false,
				activityLogCursor: undefined
			};
		}
		case RESET_ACTIVITY_LOG: {
			return {
				...state,
				activityLog: null,
				activityLogCursor: undefined,
				noMoreActivities: false
			};
		}
		case FETCH_ACTIVITY_LOG: {
			return handle(state, action, {
				start: (state) => ({
						...state,
						loadingActivityLog: true
					
				}),
				success: (state, action) => {
					const newState = { ...state };

					newState.activityLogCursor = action.payload.next ?? state.activityLogCursor;

					// Ensure no duplicates
					const activityMap = state.activityLog?.reduce(
					(acc, cur) => ({
						...acc,
						[cur.uuid]: cur,
					}),
					{} as Record<string, (typeof action.payload.activities)[0]>
					);
					const newActivities = action.payload.activities.filter((activity: IReturnableActivity) => !activityMap?.[activity.uuid]);
					const updatedActivityLog = [...(state.activityLog || []), ...newActivities].toSorted(
					(a, b) => new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime()
					);
					newState.activityLog = updatedActivityLog;

					// If the next cursor already exists in the activity log, it means we've reached the end of the log.
					// This is mostly used in conjuction with setMoreActivitiesAvailable, where the cursor is reset because
					// the log is expected to have new entries due to project changes.
					const nextAlreadyExists = !!updatedActivityLog.find((activity) => activity.timestamp_id === action.payload.next)
					?.uuid;

					if (!action.payload.next || nextAlreadyExists) {
						newState.noMoreActivities = true;
					}

					newState.activityLogCursor = nextAlreadyExists ? newState.activityLogCursor : action.payload.next;

					return newState;
				},
				always: (state) => ({
					...state,
					loadingActivityLog: false
				})
			});
		}
		default: return state;
	}
}